import {
  AccountPlanSummary,
  useAccountPlan,
  useAccountUsageSummary,
} from '@common/billing/use-account-plan';
import {PolicyFailReason} from '@common/billing/upgrade/policy-fail-reason';
import {useAuth} from '@common/auth/use-auth';
import {FormattedDate} from '@common/i18n/formatted-date';
import {Trans} from '@common/i18n/trans';
import {useSettings} from '@common/core/settings/use-settings';
import {LinkStyle} from '@common/ui/buttons/external-link';
import clsx from 'clsx';
import {ReactNode} from 'react';
import {Link} from 'react-router-dom';

interface BannerProps {
  className?: string;
}

function planRenewPath(plan: AccountPlanSummary | null): string {
  if (!plan || !plan.has_active_subscription) {
    return '/pricing';
  }

  if (plan.gateway_name === 'none') {
    return '/pricing';
  }

  return '/billing';
}

function planRenewLink(plan: AccountPlanSummary | null) {
  return (text: ReactNode) => (
    <Link className={LinkStyle} to={planRenewPath(plan)}>
      {text}
    </Link>
  );
}

function pricingLink(text: ReactNode) {
  return (
    <Link className={LinkStyle} to="/pricing">
      {text}
    </Link>
  );
}

interface BannerContentProps {
  plan: AccountPlanSummary | null;
  publishedSitesUnavailable: boolean;
  createFailReason?: PolicyFailReason;
  isSubscribed: boolean;
}

function resolveBannerContent({
  plan,
  publishedSitesUnavailable,
  createFailReason,
  isSubscribed,
}: BannerContentProps): ReactNode {
  const renewLink = planRenewLink(plan);

  if (plan?.name && !plan.has_active_subscription) {
    if (publishedSitesUnavailable) {
      return (
        <Trans
          message="Your :plan plan is not active. Your published sites are unavailable to visitors. <a>Upgrade.</a>"
          values={{
            plan: plan.name,
            a: renewLink,
          }}
        />
      );
    }

    return (
      <Trans
        message="Your :plan plan is not active. <a>Upgrade.</a>"
        values={{
          plan: plan.name,
          a: renewLink,
        }}
      />
    );
  }

  if (
    plan?.name &&
    plan.has_active_subscription &&
    plan.expires_soon &&
    plan.days_remaining !== null
  ) {
    return (
      <Trans
        message="Your :plan plan expires in [one 1 day|other :count days]. <a>Renew your plan</a>."
        values={{
          plan: plan.name,
          count: plan.days_remaining,
          a: renewLink,
        }}
      />
    );
  }

  if (publishedSitesUnavailable) {
    return (
      <Trans
        message="Your published sites are unavailable to visitors until you renew your subscription. <a>Renew subscription</a>"
        values={{
          a: (text: ReactNode) => (
            <Link className={LinkStyle} to="/billing">
              {text}
            </Link>
          ),
        }}
      />
    );
  }

  if (createFailReason === 'overQuota') {
    return (
      <Trans
        message="Your plan is at its maximum number of :name allowed. <a>Upgrade to add more.</a>"
        values={{
          name: <Trans message="sites" />,
          a: pricingLink,
        }}
      />
    );
  }

  if (createFailReason === 'noWorkspacePermission') {
    return (
      <Trans
        message="You can't create new :name in this workspace."
        values={{name: <Trans message="sites" />}}
      />
    );
  }

  if (createFailReason === 'noPermission') {
    return (
      <Trans
        message="Plan is not active. <a>Upgrade.</a>"
        values={{a: pricingLink}}
      />
    );
  }

  // Добавлено: 2026-07-04 — без активной подписки всегда показываем баннер (раньше plan=null и createAllowed=true давали пустой баннер).
  if (!isSubscribed) {
    if (plan?.name) {
      return (
        <Trans
          message="Your :plan plan is not active. <a>Upgrade.</a>"
          values={{
            plan: plan.name,
            a: renewLink,
          }}
        />
      );
    }

    return (
      <Trans
        message="Plan is not active. <a>Upgrade.</a>"
        values={{a: pricingLink}}
      />
    );
  }

  return null;
}

export function SubscriptionExpiryBanner({className}: BannerProps) {
  const {billing} = useSettings();
  const {isSubscribed} = useAuth();
  const {data: usage, isLoading} = useAccountUsageSummary();

  if (!billing.enable || isLoading) {
    return null;
  }

  const plan = usage?.plan ?? null;
  const publishedSitesUnavailable =
    !isSubscribed && (usage?.publishedUsed ?? 0) > 0;
  const createFailReason =
    usage?.createAllowed === false ? usage.createFailReason : undefined;

  const content = resolveBannerContent({
    plan,
    publishedSitesUnavailable,
    createFailReason,
    isSubscribed,
  });

  if (!content) {
    return null;
  }

  return (
    <div
      className={clsx(
        'w-full border-b border-warning/20 bg-warning/15 px-14 py-10 text-center text-sm',
        className,
      )}
      role="status"
    >
      {content}
    </div>
  );
}

export function AccountPlanMenuHeader({
  value: _menuValue,
}: {
  /** Used by Listbox collection; not rendered. */
  value?: string;
} = {}) {
  const {data: plan, isLoading} = useAccountPlan();

  if (isLoading || !plan?.name) {
    return null;
  }

  return (
    <div className="border-b px-20 py-12 text-sm">
      <div className="text-xs text-muted">
        <Trans message="Current tariff" />
      </div>
      <div className="font-medium">{plan.name}</div>
      {plan.ends_at ? (
        <div className="mt-4 text-xs text-muted">
          <Trans
            message="Valid until :date"
            values={{
              date: <FormattedDate preset="long" date={plan.ends_at} />,
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
